'use client';

import { lazy, Suspense, useRef, useState, type MouseEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import DynamicImage from '@/components/dynamic-image';
import { SvgRight } from '@/components/icons-eataly';
import { ProductTileTagOverflowRow } from '@/components/product-tile-custom/product-tile-tag-overflow-row';
import { Button } from '@/components/ui/button';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import type { ShopperProducts } from '@/scapi';
import type { SCAPIShopperProductsSchemas } from '@/types';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from 'src/components/ui/carousel';
import { PRODUCT_IMAGE_VIEW_TYPES } from '@/lib/product-image-view-types';

const BundleProductDetailsModal = lazy(() =>
    import('./bundle-product-details-modal').then((module) => ({
        default: module.BundleProductDetailsModal,
    }))
);

type Product = SCAPIShopperProductsSchemas['Product'];
type BundledProduct = ShopperProducts.schemas['BundledProduct'];

function getCardImage(product: Product) {
    return (
        product.imageGroups?.find((group) => group.viewType === PRODUCT_IMAGE_VIEW_TYPES.SN)?.images?.[0] ??
        product.imageGroups?.[0]?.images?.[0]
    );
}

function BundleContentCard({
    bundledProduct,
    onOpenDetails,
}: {
    bundledProduct: BundledProduct;
    onOpenDetails: (product: Product, trigger: HTMLButtonElement) => void;
}): ReactElement {
    const { t } = useTranslation('product');
    const product = bundledProduct.product as Product;
    const image = getCardImage(product);
    const brand = product.c_pimBrandName ?? product.brand;
    const weight = product.c_pimEtiNetWeight;
    const eatingStyles = product.c_pimEatingStyles ?? [];

    const handleOpenDetails = (event: MouseEvent<HTMLButtonElement>) => {
        onOpenDetails(product, event.currentTarget);
    };

    return (
        <div className="flex min-h-40 gap-3 rounded-2xl bg-neutral-white p-3 shadow-[0_4px_12px_0_rgba(0,0,0,0.05)]">
            <div className="flex w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-sand-95 sm:w-32 aspect-[7/10]">
                {image ? (
                    <DynamicImage
                        src={image}
                        alt={resolveImageAlt({ alt: image.alt, name: product.name })}
                        widths={[112, 224, 256]}
                        loading="lazy"
                        className="size-full"
                        imageProps={{ className: 'size-full object-fit' }}
                    />
                ) : (
                    <span className="body-copy-m text-neutral-40">{t('imageAlt')}</span>
                )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                {brand ? <p className="body-copy-m text-neutral-40">{brand}</p> : null}
                <h3 className="body-copy-l-bold line-clamp-2 text-neutral-10">{product.name}</h3>
                <ProductTileTagOverflowRow
                    highlight={product.c_pimProductHighlights}
                    eatingStyles={eatingStyles}
                    className="mt-auto"
                />
                <div className="flex items-end justify-between gap-2 pt-2">
                    <div className="body-copy-s flex flex-col gap-4">
                        <p className="body-copy-m text-muted-foreground">
                            {t('bundleContents.quantity', { count: bundledProduct.quantity ?? 1 })}
                        </p>
                        {weight ? <p className="text-neutral-40 body-copy-s">{weight}</p> : null}
                    </div>
                    <Button
                        type="button"
                        hierarchy="primary"
                        mode="dark"
                        size="icon-small"
                        iconOnly
                        aria-label={t('bundleContents.openDetails', {
                            productName: product.name ?? t('productDetails'),
                        })}
                        onClick={handleOpenDetails}>
                        <SvgRight className="size-5" aria-hidden />
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function BundleContents({ bundledProducts }: { bundledProducts: BundledProduct[] }): ReactElement {
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [modalLoaded, setModalLoaded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const openDetails = (product: Product, trigger: HTMLButtonElement) => {
        triggerRef.current = trigger;
        setSelectedProduct(product);
        setModalLoaded(true);
        setModalOpen(true);
    };

    const handleOpenChange = (open: boolean) => {
        setModalOpen(open);
        if (!open) {
            window.requestAnimationFrame(() => triggerRef.current?.focus());
        }
    };

    return (
        <>
            <div className="flex flex-col gap-4 lg:hidden">
                {bundledProducts.map((bundledProduct) => (
                    <BundleContentCard
                        key={`${bundledProduct.product.id}-${bundledProduct.id ?? 'bundle-item'}`}
                        bundledProduct={bundledProduct}
                        onOpenDetails={openDetails}
                    />
                ))}
            </div>
            <Carousel
                opts={{
                    align: 'start',
                }}
                // workaround for the carousel to work with the grid-global layout constraints
                className="flex flex-col gap-6 relative w-dvw left-1/2 -translate-x-1/2 overflow-hidden max-lg:hidden [&>div]:lg:px-[8.333333333333334%] [&>div]:py-2">
                <CarouselContent className="gap-2 ml-0 col-span-full">
                    {bundledProducts.map((bundledProduct) => (
                        <CarouselItem
                            key={`${bundledProduct.product.id}-${bundledProduct.id ?? 'bundle-item'}`}
                            className="basis-[390px] ps-0!">
                            <BundleContentCard
                                key={`${bundledProduct.product.id}-${bundledProduct.id ?? 'bundle-item'}`}
                                bundledProduct={bundledProduct}
                                onOpenDetails={openDetails}
                            />
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <div className="flex items-center justify-end gap-4 w-fit">
                    <CarouselPrevious hideIfNoScroll size="large" className="static translate-x-0 translate-y-0" />
                    <CarouselNext hideIfNoScroll size="large" className="static translate-x-0 translate-y-0" />
                </div>
            </Carousel>
            {modalLoaded && selectedProduct ? (
                <Suspense fallback={null}>
                    <BundleProductDetailsModal
                        product={selectedProduct}
                        open={modalOpen}
                        onOpenChange={handleOpenChange}
                    />
                </Suspense>
            ) : null}
        </>
    );
}
